import { ReactNode, useMemo } from 'react';
import { BoxProps, Grid, componentWithRef } from '@robocorp/components';
import { styled } from '@robocorp/theme';

import { Item } from './components/Item';
import { Ticker } from './components/Ticker';
import { ValuesContext } from './context';
import type { ValuesSize } from './types';

type Props = {
  children?: ReactNode;
  columns: number | number[];
  size?: ValuesSize;
} & BoxProps;

const compoundComponents = {
  Item,
  Ticker,
};

const Container = styled(Grid)<{ $size?: ValuesSize }>`
  margin-left: -${({ $size, theme }) => ($size === 'large' ? theme.space.$16 : theme.space.$8)};
  width: calc(
    100% + ${({ $size, theme }) => ($size === 'large' ? theme.space.$32 : theme.space.$16)}
  );

  ${({ theme }) => theme.screen.s} {
    margin-left: 0px;
    width: 100%;
  }
`;

/**
 * Component to display main statistical values with actions in a grid like layout
 *
 * @example
 * <Values columns={[1, 1, 2, 3]}>
 *   <Values.Item label="Label" onClick={onClick}>Value</Values.Item>
 * </Values>
 */
export const Values = componentWithRef<Props, HTMLUListElement, typeof compoundComponents>(
  ({ children, columns, size, ...rest }, forwardedRef) => {
    const contextValue = useMemo(() => {
      return { size: size || 'medium' };
    }, [size]);

    return (
      <Container gap="$24" as="ul" $size={size} columns={columns} {...rest} ref={forwardedRef}>
        <ValuesContext.Provider value={contextValue}>{children}</ValuesContext.Provider>
      </Container>
    );
  },
  compoundComponents,
);

Values.defaultProps = {
  size: 'medium',
};
